import React, { useEffect, useState } from "react";
import { Modal, Table, Tag, Spin, Alert, Row, Col, Statistic, message } from "antd";
import { useAtom } from "jotai";
import moment from "moment";
import { HistoricoDescansoMedicoService } from "services/VacacionesService";
import { detalleSeleccionadoAtom } from "../../DescansoMedicoStore";

const formatoDias = (valor) => (valor === null || valor === undefined ? "-" : Number(valor).toString());
const formatoFecha = (valor) => (valor ? moment(valor).format("DD/MM/YYYY") : "-");

const colorEstado = { APROBADO: "green", PENDIENTE: "gold" };

const ModalDetalleDescansoMedico = () => {
	const [seleccionado, setSeleccionado] = useAtom(detalleSeleccionadoAtom);
	const [detalle, setDetalle] = useState(undefined);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		const cargarDetalle = async () => {
			setLoading(true);
			try {
				const respuesta = await HistoricoDescansoMedicoService.detalle({
					colaborador: seleccionado.colaboradorNumeroDocumentoIdentidad,
					anio: seleccionado.anio,
				});
				if (respuesta.estado === "EXITO") {
					setDetalle(respuesta.data);
				} else {
					message.error(respuesta.mensaje || "No se pudo cargar el detalle.");
				}
			} catch (error) {
				message.error("No se pudo cargar el detalle.");
			} finally {
				setLoading(false);
			}
		};
		if (seleccionado) {
			cargarDetalle();
		} else {
			setDetalle(undefined);
		}
	}, [seleccionado]);

	const resumen = detalle?.resumen;

	const columnas = [
		{ title: "Fecha Inicio", align: "center", render: (_, f) => formatoFecha(f.fechaInicio) },
		{ title: "Fecha Fin", align: "center", render: (_, f) => formatoFecha(f.fechaFin) },
		{ title: "Fecha Solicitud", align: "center", render: (_, f) => formatoFecha(f.fechaSolicitud) },
		{ title: "Días", dataIndex: "diasSolicitados", align: "center", render: formatoDias },
		{ title: "Pagados", dataIndex: "diasPagados", align: "center", render: formatoDias },
		{ title: "Subsidiados", dataIndex: "diasSubsidiados", align: "center", render: formatoDias },
		{ title: "Tipo Enfermedad", dataIndex: "tipoEnfermedad", align: "center", render: (v) => v || "-" },
		{
			title: "Estado",
			dataIndex: "estado",
			align: "center",
			render: (estado, fila) => (
				<Tag color={colorEstado[estado] || "default"}>
					{estado}
					{!fila.contabiliza && " (no suma)"}
				</Tag>
			),
		},
	];

	return (
		<Modal
			title={`Detalle Descanso Médico${resumen ? ` - ${resumen.colaboradorNombreCompleto || ""}` : ""}`}
			open={!!seleccionado}
			onCancel={() => setSeleccionado(undefined)}
			onOk={() => setSeleccionado(undefined)}
			cancelButtonProps={{ style: { display: "none" } }}
			okText="Cerrar"
			width="70%"
			destroyOnClose={true}
		>
			<Spin spinning={loading}>
				{resumen && (
					<React.Fragment>
						<Row gutter={16} style={{ textAlign: "center", marginBottom: "16px" }}>
							<Col span={6}>
								<Statistic
									title={`Total acumulado ${resumen.anio}`}
									value={formatoDias(resumen.diasAcumulados)}
									suffix={`/ ${formatoDias(detalle.umbralSubsidio)}`}
									valueStyle={{ color: resumen.enSubsidio ? "#cf1322" : "#108ee9" }}
								/>
							</Col>
							<Col span={6}>
								<Statistic title="Histórico (Excel)" value={formatoDias(resumen.diasHistorico)} />
							</Col>
							<Col span={6}>
								<Statistic title="Registrados en el sistema" value={formatoDias(resumen.diasSistema)} />
							</Col>
							<Col span={6}>
								<Statistic title="Subsidiados" value={formatoDias(resumen.diasSubsidiados)} />
							</Col>
						</Row>

						{resumen.enSubsidio ? (
							<Alert
								type="error"
								showIcon
								style={{ marginBottom: "12px" }}
								message={
									resumen.continuaSubsidioAnioAnterior
										? "Continúa en subsidio desde el año anterior."
										: `Alcanzó los ${formatoDias(detalle.umbralSubsidio)} días: los días siguientes se gestionan como subsidio.`
								}
							/>
						) : (
							<Alert
								type="info"
								showIcon
								style={{ marginBottom: "12px" }}
								message={`Faltan ${formatoDias(detalle.diasParaUmbral)} día(s) para llegar a los ${formatoDias(
									detalle.umbralSubsidio
								)} días de subsidio.`}
							/>
						)}

						{resumen.tieneHistorico && (
							<div style={{ marginBottom: "12px" }}>
								<strong>Histórico cargado:</strong> {formatoDias(resumen.diasHistorico)} día(s) acumulados,{" "}
								{formatoDias(detalle.diasHistoricoSubsidiados)} subsidiados
								{resumen.observacion ? ` - ${resumen.observacion}` : ""}
							</div>
						)}

						<Table
							rowKey="solicitudId"
							dataSource={detalle.solicitudes}
							columns={columnas}
							size="small"
							pagination={false}
							locale={{ emptyText: "Sin solicitudes de descanso médico en el sistema este año" }}
						/>
						<div style={{ marginTop: "8px", fontSize: "12px", color: "#888" }}>
							Las solicitudes pendientes no suman al total hasta que sean aprobadas. Cada solicitud se asigna al año en que
							inicia.
						</div>
					</React.Fragment>
				)}
			</Spin>
		</Modal>
	);
};

export default ModalDetalleDescansoMedico;
